/**
 * src/components/spatial/useUtterancePlayer.ts
 *
 * Playing what an object just said.
 *
 * Extracted because two surfaces now speak — the operator's chat panel
 * and the visitor's card on the map — and speech that behaved
 * differently in the two of them would be a bug waiting to be reported
 * once and fixed once. The rules it keeps are the awkward ones: exactly
 * one object URL alive at a time, revoked before the next replaces it
 * and again when the component goes away, and a refusal from the
 * browser treated as an answer rather than something to argue with.
 *
 * Autoplay policy is the browser's to enforce. A visitor who has not
 * clicked anything gets silence and the text, which is the correct
 * outcome and not a failure to route around.
 *
 * `speaking` was added for barge-in. Whether to cut the object off when a
 * visitor starts talking is the operator's decision, carried on the session
 * as `interruptMode`, and acting on it needs to know whether anything is
 * actually being said. Tracked from the element's own events rather than from
 * a flag set at call time, because the two disagree in the case that matters:
 * autoplay refusal means `play` was called and nothing is playing.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { objectUrlForAudio } from '../../utils/audioObjectUrl';

export interface UtterancePlayer {
  /** Play one utterance. Silently does nothing if the audio is unusable. */
  play: (audioBase64: string, mime: string | null) => void;
  /** Stop whatever is playing, e.g. when a conversation is closed. */
  stop: () => void;
  /**
   * Whether audio is playing right now.
   *
   * False when the browser refused to start, which is the point: a caller
   * deciding whether to interrupt must not interrupt silence.
   */
  speaking: boolean;
}

/**
 * @param onRefused Told when the browser declined to play, so the
 *   caller can say so in words instead of appearing to do nothing.
 */
export function useUtterancePlayer(onRefused?: (reason: string) => void): UtterancePlayer {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const refusedRef = useRef(onRefused);
  refusedRef.current = onRefused;

  const [speaking, setSpeaking] = useState(false);

  useEffect(() => () => {
    try { audioRef.current?.pause(); } catch { /* already gone */ }
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
  }, []);

  /**
   * The element, created once and wired to report its own state.
   *
   * The listeners are attached here rather than per play so that a second
   * utterance does not accumulate another set of them.
   */
  const element = useCallback((): HTMLAudioElement => {
    if (audioRef.current) return audioRef.current;
    const audio = new Audio();
    audio.addEventListener('playing', () => setSpeaking(true));
    audio.addEventListener('ended', () => setSpeaking(false));
    audio.addEventListener('pause', () => setSpeaking(false));
    audio.addEventListener('error', () => setSpeaking(false));
    audioRef.current = audio;
    return audio;
  }, []);

  const play = useCallback((audioBase64: string, mime: string | null) => {
    const url = objectUrlForAudio(audioBase64, mime);
    if (!url) return;

    const audio = element();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    audio.src = url;

    void audio.play().then(
      () => {
        // jsdom, and some browsers, resolve `play()` without ever firing
        // `playing`. Treated as speaking on the resolve so barge-in is not
        // silently disabled wherever that happens.
        setSpeaking(true);
      },
      () => {
        setSpeaking(false);
        refusedRef.current?.('the browser refused playback; the reply is above as text');
      },
    );
  }, [element]);

  const stop = useCallback(() => {
    try { audioRef.current?.pause(); } catch { /* already gone */ }
    // Set directly as well as on the `pause` event: pausing an element that
    // never started fires nothing, and a caller that just asked for silence
    // should see silence.
    setSpeaking(false);
  }, []);

  return { play, stop, speaking };
}

export default useUtterancePlayer;
